import { type QueryClient, queryOptions } from '@tanstack/react-query'
import type { KambiSportName } from '@mobi/api-types/kambi'
import {
  getKambiBetOffersByEventIds,
  getKambiBetOffersByGroupIds,
  getKambiBetOffersByIds,
} from '../api/betOffers'
import { getPreMatchCategoryForSport } from '../api/categories'
import { getEventsForGroupId, getUpcomingEventsForAllSports } from '../api/events'
import { getEventGroupsFromRoot, getGroupHighlights } from '../api/groups'
import {
  getEventsBySportTermKey,
  getEventsForCompetition,
  getFuturesEventsForCompetition,
} from '../api/listView'
import { getKambiEventPricing } from '../api/onDemandPricing'
import { sportsOfferingQueryKeys } from './keys'

export const getEventGroupsFromRootQuery = queryOptions({
  queryFn: getEventGroupsFromRoot,
  queryKey: sportsOfferingQueryKeys.EventGroupsFromRoot,
  refetchOnWindowFocus: false,
  staleTime: 1_000 * 60 * 2, // 2min
})

export const getMatchesBySportTermKeyQuery = (sport: string, categoryId: number | undefined) =>
  queryOptions({
    queryFn: () => getEventsBySportTermKey(sport, categoryId),
    queryKey: sportsOfferingQueryKeys.EventsForSport(sport, categoryId),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getGroupHighlightsQuery = queryOptions({
  queryFn: getGroupHighlights,
  queryKey: sportsOfferingQueryKeys.EventGroupHighlights,
  refetchOnWindowFocus: false,
  staleTime: 1_000 * 60 * 2, // 2min
})

export const getUpcomingEventsForAllSportsQuery = queryOptions({
  queryFn: getUpcomingEventsForAllSports,
  queryKey: sportsOfferingQueryKeys.UpcomingEventsForAllSports,
  refetchOnWindowFocus: false,
  staleTime: 1_000 * 60 * 2, // 2min
  refetchOnMount: true,
})

export const getPreMatchCategoryForSportQuery = (sport: KambiSportName | undefined) =>
  queryOptions({
    queryFn: () => (sport ? getPreMatchCategoryForSport(sport) : undefined),
    queryKey: ['sport', 'pre-match-category', sport],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2,
    enabled: !!sport,
  })

export const getKambiBetOffersByIdsQuery = (betOfferIds: number[]) =>
  queryOptions({
    queryFn: () => getKambiBetOffersByIds({ betOfferIds }),
    queryKey: sportsOfferingQueryKeys.BetOffersByIds(betOfferIds),
    staleTime: 1_000 * 60 * 10, // 10min
  })

export const getKambiBetOffersByEventIdQuery = (eventId: number) =>
  queryOptions({
    queryFn: () => getKambiBetOffersByEventIds({ eventIds: [eventId] }),
    queryKey: sportsOfferingQueryKeys.BetOffersByEvents([eventId]),
    refetchOnWindowFocus: false,
  })

export const getKambiBetOffersByGroupIdQuery = (groupId: number) =>
  queryOptions({
    queryFn: () => getKambiBetOffersByGroupIds({ groupIds: [groupId] }),
    queryKey: sportsOfferingQueryKeys.BetOffersByGroups([groupId]),
    refetchOnWindowFocus: false,
  })

export const getKambiBetOffersByEventIdsQuery = (eventIds: number[]) =>
  queryOptions({
    queryFn: () =>
      eventIds.length !== 0 ? getKambiBetOffersByEventIds({ eventIds }) : Promise.resolve(null),
    queryKey: sportsOfferingQueryKeys.BetOffersByEvents(eventIds),
    staleTime: 1_000 * 60 * 10, // 10min
  })

export const getMatchesForCompetitionByCategoryQuery = (
  sport: string,
  region: string,
  competition: string,
  categoryid?: number
) =>
  queryOptions({
    queryFn: () => getEventsForCompetition(sport, region, competition, categoryid),
    queryKey: [
      sportsOfferingQueryKeys.EventsForCompetition(sport, region, competition),
      'matches',
      categoryid,
    ],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getFuturesMatchesForCompetitionQuery = (
  sport: string,
  region: string,
  competition: string
) =>
  queryOptions({
    queryFn: () => getFuturesEventsForCompetition(sport, region, competition),
    queryKey: [sportsOfferingQueryKeys.EventsForCompetition(sport, region, competition), 'futures'],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const eventsForCompetitionIdQuery = (competitionId: number) =>
  queryOptions({
    queryFn: () => getEventsForGroupId(competitionId),
    queryKey: sportsOfferingQueryKeys.EventsForGroupId(competitionId),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

export const getHasSGMForGroupsQuery = (groupIds: number[], queryClient: QueryClient) => {
  const sortedGroupIds = [...new Set(groupIds)].sort((a, b) => a - b)

  return queryOptions({
    queryFn: async () => {
      const groupEventsResults = await Promise.all(
        sortedGroupIds.map(async groupId => {
          const eventsData = await queryClient.fetchQuery(eventsForCompetitionIdQuery(groupId))

          const eventIds = (eventsData.events ?? [])
            .filter(event => event.tags.includes('BET_BUILDER'))
            .map(event => event.id)

          return { groupId, eventIds }
        })
      )

      const groupHasSGMMap: Record<number, boolean> = Object.fromEntries(
        sortedGroupIds.map(groupId => [groupId, false])
      )

      const allEventIds = Array.from(new Set(groupEventsResults.flatMap(result => result.eventIds)))

      if (allEventIds.length === 0) {
        return groupHasSGMMap
      }

      const sgmEventIds = await getSgmEventIds(allEventIds)

      for (const { groupId, eventIds } of groupEventsResults) {
        groupHasSGMMap[groupId] = eventIds.some(eventId => sgmEventIds.has(eventId))
      }

      return groupHasSGMMap
    },
    queryKey: ['sgm-availability-for-groups', ...sortedGroupIds],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 5, // 5min
    retry: false,
  })
}

export const getHasSGMForEventsQuery = (eventIds: number[]) => {
  const sortedEventIds = [...new Set(eventIds)].sort((a, b) => a - b)

  return queryOptions({
    queryFn: async () => {
      const eventHasSGMMap: Record<number, boolean> = Object.fromEntries(
        sortedEventIds.map(eventId => [eventId, false])
      )

      if (sortedEventIds.length === 0) {
        return eventHasSGMMap
      }

      const sgmEventIds = await getSgmEventIds(sortedEventIds)

      for (const eventId of sortedEventIds) {
        eventHasSGMMap[eventId] = sgmEventIds.has(eventId)
      }

      return eventHasSGMMap
    },
    queryKey: ['sgm-availability-for-events', ...sortedEventIds],
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 5, // 5min
  })
}

export const getKambiEventPricingQuery = (eventId: number) =>
  queryOptions({
    queryFn: () => getKambiEventPricing(eventId),
    queryKey: sportsOfferingQueryKeys.EventPricingForEvent(eventId),
    refetchOnWindowFocus: false,
    staleTime: 1_000 * 60 * 2, // 2min
  })

// =============
// Local Helpers
// =============

const getSgmEventIds = async (eventIds: number[]): Promise<Set<number>> => {
  const eventIdChunks = chunkEventIds(eventIds, 130)

  const pricingResults = await Promise.all(
    eventIdChunks.map(eventIdChunk => getKambiEventPricing(eventIdChunk))
  )

  return new Set(
    pricingResults
      .flatMap(result => result.pricingList ?? [])
      .filter(pricing => (pricing.combinableOutcomeIds?.length ?? 0) > 0)
      .map(pricing => pricing.eventId)
      .filter((eventId): eventId is number => eventId != null)
  )
}

const chunkEventIds = (eventIds: number[], chunkSize: number): number[][] => {
  const chunks: number[][] = []

  for (let i = 0; i < eventIds.length; i += chunkSize) {
    chunks.push(eventIds.slice(i, i + chunkSize))
  }

  return chunks
}
